import { iconSetQuartzLight, themeQuartz } from "ag-grid-community"
import { getTailwindValue } from "~/utils/tailwind"

export const createCustomTheme = (columnBorder = true, compact = false) =>
  themeQuartz
    .withPart(iconSetQuartzLight)
    .withParams({
      borderRadius: "8px",

      checkboxBorderWidth: 0,
      checkboxBorderRadius: "4px",

      wrapperBorder: "none",
      browserColorScheme: "light",

      fontFamily: {
        googleFont: "Inter",
      },
      fontSize: compact ? "12.5px" : "13px",

      headerFontSize: compact ? "11px" : "12px",
      headerFontWeight: 500,
      pinnedRowFontWeight: 400,

      headerVerticalPaddingScale: compact ? 0.55 : 0.75,
      rowVerticalPaddingScale: compact ? 0.45 : 0.8,
      iconSize: compact ? "16px" : "20px",
      oddRowBackgroundColor: undefined,

      wrapperBorderRadius: "0px",
      columnBorder: columnBorder,
      menuBorder: false,
      headerColumnBorderHeight: "100%",
      toolPanelSeparatorBorder: false,
    })
    .withParams(
      {
        // Palette values below mirror the new design-system --theme-* tokens
        // (see styles/globals.css [data-theme="light"]). They can't be read as
        // --theme-* via getTailwindValue because both light/dark param blocks
        // resolve against the active theme, so we map each token to its palette
        // value per mode instead.
        accentColor: getTailwindValue("--color-purpleheart-500"), // --theme-background-interactive
        backgroundColor: getTailwindValue("--color-white"), // --theme-background-base
        borderColor: getTailwindValue("--color-gray-200"), // --theme-border-base
        rangeSelectionBorderColor: getTailwindValue("--color-gray-300"), // --theme-border-strong

        checkboxUncheckedBackgroundColor: getTailwindValue("--color-gray-50"), // --theme-background-component
        checkboxCheckedBackgroundColor: getTailwindValue("--color-purpleheart-500"), // --theme-background-interactive
        checkboxCheckedShapeColor: getTailwindValue("--color-white"), // --theme-fg-on-color
        checkboxIndeterminateBackgroundColor: getTailwindValue("--color-purpleheart-500"), // --theme-background-interactive
        checkboxIndeterminateShapeColor: getTailwindValue("--color-white"), // --theme-fg-on-color

        foregroundColor: getTailwindValue("--color-gray-950"), // --theme-fg-base
        headerBackgroundColor: getTailwindValue("--color-gray-100"), // --theme-background-base-hover
        headerTextColor: getTailwindValue("--color-gray-600"), // --theme-fg-subtle
        headerColumnBorder: columnBorder
          ? { color: getTailwindValue("--color-gray-200") } // --theme-border-base
          : undefined,
        headerColumnResizeHandleColor: "#00000000",

        // No selectedRowBackgroundColor here: the purple tint denotes the HIGHLIGHTED rows (the
        // ones an inspector is detailing, applied as a CSS class on the row element). Checkbox
        // selection is shown by the checkbox itself — tinting it too would make the two row states
        // indistinguishable.
        rowHoverColor: getTailwindValue("--color-gray-100"), // --theme-background-component-hover

        pinnedRowBackgroundColor: getTailwindValue("--color-gray-100"), // --theme-background-base-hover

        popupShadow:
          "0 0 0 1px rgba(0, 0, 0, 0.08), 0 4px 8px 0 rgba(0, 0, 0, 0.08), 0 8px 16px 0 rgba(0, 0, 0, 0.08)",
        menuShadow:
          "0 0 0 1px rgba(0, 0, 0, 0.08), 0 4px 8px 0 rgba(0, 0, 0, 0.08), 0 8px 16px 0 rgba(0, 0, 0, 0.08)",
      },
      "light",
    )
    .withParams(
      {
        // Palette values below mirror the new design-system --theme-* tokens
        // (see styles/globals.css [data-theme="dark"]).
        accentColor: getTailwindValue("--color-purpleheart-500"), // --theme-background-interactive
        backgroundColor: getTailwindValue("--color-gray-900"), // --theme-background-base
        borderColor: getTailwindValue("--color-white-alpha-10"), // --theme-border-base
        rangeSelectionBorderColor: getTailwindValue("--color-white-alpha-16"), // --theme-border-strong

        checkboxUncheckedBackgroundColor: getTailwindValue("--color-gray-800"), // --theme-background-component
        checkboxCheckedBackgroundColor: getTailwindValue("--color-purpleheart-500"), // --theme-background-interactive
        checkboxCheckedShapeColor: getTailwindValue("--color-white"), // --theme-fg-on-color
        checkboxIndeterminateBackgroundColor: getTailwindValue("--color-purpleheart-500"), // --theme-background-interactive
        checkboxIndeterminateShapeColor: getTailwindValue("--color-white"), // --theme-fg-on-color

        foregroundColor: getTailwindValue("--color-gray-100"), // --theme-fg-base
        headerBackgroundColor: getTailwindValue("--color-gray-800"), // --theme-background-base-hover
        headerTextColor: getTailwindValue("--color-gray-400"), // --theme-fg-subtle
        headerColumnBorder: columnBorder
          ? { color: getTailwindValue("--color-white-alpha-10") } // --theme-border-base
          : undefined,

        // See the light block: no selected-row tint; purple denotes the highlighted row.
        rowHoverColor: getTailwindValue("--color-white-alpha-10"), // --theme-background-component-hover

        pinnedRowBackgroundColor: getTailwindValue("--color-gray-800"), // --theme-background-base-hover

        headerColumnResizeHandleColor: "#00000000",
        popupShadow:
          "0 0 0 1px rgba(0, 0, 0, 0.08), 0 4px 8px 0 rgba(0, 0, 0, 0.08), 0 8px 16px 0 rgba(0, 0, 0, 0.08)",
        menuShadow:
          "0 0 0 1px rgba(0, 0, 0, 0.08), 0 4px 8px 0 rgba(0, 0, 0, 0.08), 0 8px 16px 0 rgba(0, 0, 0, 0.08)",
        browserColorScheme: "dark",
      },
      "dark",
    )
